/**
 * Excuse Types catalog (M17.9) at /policies/excuse-types. Lists active
 * excuse types per legal entity (?le=<uuid>). 5 defaults are auto-seeded
 * by the M7 onboarding wizard hook (LATE_ARRIVAL / EARLY_DEPARTURE /
 * PERMISSION / SICK_HALF / PERSONAL).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, EntityDrawer, PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import type { ExcuseTypeDetail } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const CATEGORIES = ["late", "early", "permission", "sick", "personal"] as const;

function NewExcuseTypeDialog({
  legalEntityId,
  onClose,
  onCreated,
}: {
  legalEntityId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    code: "",
    name_en: "",
    name_ar: "",
    excuse_category: "permission" as (typeof CATEGORIES)[number],
    max_per_month: "",
    max_per_year: "",
    requires_attachment: false,
    paid: true,
    affects_attendance: true,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.createExcuseType({
        legal_entity_id: legalEntityId,
        code: form.code,
        name_en: form.name_en,
        name_ar: form.name_ar,
        excuse_category: form.excuse_category,
        max_per_month: form.max_per_month ? parseInt(form.max_per_month, 10) : null,
        max_per_year: form.max_per_year ? parseInt(form.max_per_year, 10) : null,
        requires_attachment: form.requires_attachment,
        paid: form.paid,
        affects_attendance: form.affects_attendance,
      }),
    onSuccess: () => onCreated(),
    onError: (err) =>
      setErrorMsg(err instanceof ApiError ? err.detail : String(err)),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("policies.excuse_types_new")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-excuse-type-form"
            disabled={m.isPending}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {m.isPending
              ? t("policies.creating")
              : t("policies.create")}
          </button>
        </div>
      }
    >
        <form
          id="new-excuse-type-form"
          onSubmit={(e) => {
            e.preventDefault();
            setErrorMsg(null);
            m.mutate();
          }}
          className="flex flex-col gap-3 text-sm"
        >
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.code_label")}</span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.allowances_category_label")}</span>
              <select
                value={form.excuse_category}
                onChange={(e) =>
                  setForm({ ...form, excuse_category: e.target.value as (typeof CATEGORIES)[number] })
                }
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.name_en_label")}</span>
              <input
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.name_ar_label")}</span>
              <input
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                dir="rtl"
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                required
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.excuse_types_max_month_short")}</span>
              <input
                type="number"
                min={0}
                value={form.max_per_month}
                onChange={(e) => setForm({ ...form, max_per_month: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                placeholder={t("policies.excuse_types_unlimited_placeholder")}
              />
            </label>
            <label>
              <span className="text-xs font-medium uppercase text-text-secondary">{t("policies.excuse_types_max_year_short")}</span>
              <input
                type="number"
                min={0}
                value={form.max_per_year}
                onChange={(e) => setForm({ ...form, max_per_year: e.target.value })}
                className="mt-1 w-full rounded border border-border-default px-3 py-2"
                placeholder={t("policies.excuse_types_unlimited_placeholder")}
              />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={form.requires_attachment}
                onChange={(e) => setForm({ ...form, requires_attachment: e.target.checked })}
              />
              {t("policies.excuse_types_requires_attachment")}
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={form.paid}
                onChange={(e) => setForm({ ...form, paid: e.target.checked })}
              />
              {t("policies.excuse_types_paid")}
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={form.affects_attendance}
                onChange={(e) => setForm({ ...form, affects_attendance: e.target.checked })}
              />
              {t("policies.excuse_types_affects_attendance")}
            </label>
          </div>
          {errorMsg && <p className="rounded bg-rose-50 p-2 text-rose-700">{errorMsg}</p>}
        </form>
    </EntityDrawer>
  );
}

function ExcuseTypeRow({ et, onChanged }: { et: ExcuseTypeDetail; onChanged: () => void }) {
  const { t } = useTranslation();
  const toggleM = useMutation({
    mutationFn: () => api.updateExcuseType(et.id, { is_active: !et.is_active }),
    onSuccess: onChanged,
  });
  return (
    <article className="flex items-center gap-3 rounded-lg border border-border-subtle bg-surface-default p-3 text-sm">
      <ShieldAlert size={20} className="text-text-secondary" />
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/policies/excuse-types/${et.id}`}
            className="font-semibold hover:underline"
          >
            {et.name_en}
          </Link>
          <span className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-mono">{et.code}</span>
          <span className="rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">{et.excuse_category}</span>
          {!et.is_active && (
            <span className="rounded bg-surface-muted px-1.5 py-0.5 text-xs text-text-secondary">{t("policies.excuse_types_inactive_pill")}</span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-text-secondary">
          {t("policies.excuse_types_row_summary", {
            month: et.max_per_month ?? "∞",
            year: et.max_per_year ?? "∞",
          })}
          {et.requires_attachment && t("policies.excuse_types_requires_attachment_suffix")}
          {!et.paid && t("policies.excuse_types_unpaid_suffix")}
          {!et.affects_attendance && t("policies.excuse_types_no_attendance_suffix")}
        </div>
      </div>
      <button
        type="button"
        onClick={() => toggleM.mutate()}
        disabled={toggleM.isPending}
        className="rounded border border-border-default px-2 py-1 text-xs hover:bg-surface-muted disabled:opacity-50"
      >
        {et.is_active
          ? t("policies.allowances_deactivate")
          : t("policies.allowances_activate")}
      </button>
    </article>
  );
}

export function ExcuseTypesPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const [showNew, setShowNew] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["excuse-types", le, includeInactive],
    queryFn: () => api.listExcuseTypes(le, { includeInactive }),
    enabled: !!le,
  });
  if (gate) return gate;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const refresh = () => qc.invalidateQueries({ queryKey: ["excuse-types", le] });
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.excuse_types")}
        breadcrumbs={[
          { label: t("nav.policies"), href: "/policies" },
          { label: t("nav.excuse_types") },
        ]}
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("common.show_inactive")}
            </label>
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1 rounded-sm bg-brand px-3 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover"
            >
              <Plus size={14} /> {t("common.new")}
            </button>
          </div>
        }
      />
      {(q.data ?? []).length === 0 ? (
        <EmptyState
          icon={ShieldAlert}
          headline={t("policies.excuse_types_empty")}
          description={t("policies.excuse_types_empty_body")}
        />
      ) : (
        <div className="flex flex-col gap-2">
          {(q.data ?? []).map((et) => (
            <ExcuseTypeRow key={et.id} et={et} onChanged={refresh} />
          ))}
        </div>
      )}
      {showNew && (
        <NewExcuseTypeDialog
          legalEntityId={le}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
